// Auto-stage advancement threshold rules editor — Batch 15.2.
// Per Brian's pre-positioning: JSON-shaped configurable thresholds, not
// hardcoded. Operator iterates without engineering involvement.
// Brain: [skills/land-intelligence].
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || '';

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const STAGES = [
  'discovered', 'screening', 'qualified', 'diligence', 'loi', 'contract',
];

const FIELD_HINTS = [
  { field: 'ahp_composite_score', desc: 'AHP-weighted composite (0-100)' },
  { field: 'composite_score', desc: 'Deal-economics composite (0-100)' },
  { field: 'speed_to_mw_months', desc: 'Months from acquisition to live MW' },
  { field: 'revenue_acceleration_value_usd', desc: 'Acceleration vs market' },
  { field: 'acreage', desc: 'Parcel acreage' },
  {
    field: 'infrastructure_proximity.nearest_substation_distance_mi',
    desc: 'OSM-resolved substation distance (mi). Dotted path because it lives in the JSON column.',
  },
];

export default function LandStageRules() {
  const [config, setConfig] = useState(null);
  const [rulesText, setRulesText] = useState('');
  const [name, setName] = useState('default');
  const [notes, setNotes] = useState('');
  const [parseError, setParseError] = useState(null);
  const [serverError, setServerError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setServerError(null);
    try {
      const resp = await fetch(`${API_BASE}/land/stage-thresholds`, { headers: authHeaders() });
      if (!resp.ok) throw new Error(`stage-thresholds: ${resp.status}`);
      const body = await resp.json();
      setConfig(body);
      setRulesText(JSON.stringify(body.rules || [], null, 2));
      setName(body.is_default ? 'default' : (body.name || 'default'));
      setNotes(body.notes || '');
      setSavedAt(body.updated_at || null);
    } catch (e) {
      setServerError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onTextChange = (e) => {
    setRulesText(e.target.value);
    // Validate JSON locally on every keystroke; clear server-side error
    // so operator can see whether their edit is structurally valid.
    setServerError(null);
    try {
      const parsed = JSON.parse(e.target.value);
      if (!Array.isArray(parsed)) {
        setParseError('Rules must be a JSON array');
        return;
      }
      setParseError(null);
    } catch (err) {
      setParseError(err.message);
    }
  };

  const save = async () => {
    if (parseError) return;
    setBusy(true);
    setServerError(null);
    try {
      const rules = JSON.parse(rulesText);
      const resp = await fetch(`${API_BASE}/land/stage-thresholds`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ name, rules, notes: notes || null }),
      });
      if (!resp.ok) {
        const detail = (await resp.json().catch(() => ({})))?.detail || resp.statusText;
        throw new Error(detail);
      }
      const body = await resp.json();
      setSavedAt(body.updated_at);
      setConfig({ ...body, is_default: false });
    } catch (e) {
      setServerError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm('Discard your edits and reload the saved config?')) return;
    await load();
    setParseError(null);
  };

  if (loading) {
    return <div className="p-10 text-center text-sm text-zinc-500">Loading threshold rules…</div>;
  }

  return (
    <div className="min-h-screen bg-zinc-50 p-6 lg:p-10">
      <div className="mx-auto max-w-5xl">
        <div className="mb-4 flex items-center gap-2 text-sm text-zinc-500">
          <Link to="/land-intelligence/pipeline" className="hover:text-zinc-900">
            ← Pipeline
          </Link>
        </div>

        <header className="mb-6 rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
            Land Intelligence — Auto-stage advancement (Batch 15.2)
          </p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-zinc-900">
            Threshold rules
          </h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-zinc-600">
            JSON-shaped rules that auto-promote parcels through the
            pipeline nightly (04:30 UTC). Each rule fires when its
            <code className="mx-1 rounded bg-zinc-100 px-1 font-mono text-xs">from_stage</code>
            matches the parcel AND every condition passes (AND within rule).
            Multiple rules per from_stage are allowed; first match fires
            (rules evaluate in list order). Promotion is one-way only —
            the auto-advancer never demotes; manual demotion stays in the
            parcel detail view.
          </p>
          {config?.is_default && (
            <p className="mt-2 inline-block rounded-lg border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800">
              Defaults shown — no saved config yet. Edit and save to activate.
            </p>
          )}
        </header>

        {serverError && (
          <div className="mb-4 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
            {serverError}
          </div>
        )}

        <section className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-3 lg:gap-4">
            <label className="block lg:col-span-1">
              <span className="text-xs font-semibold text-zinc-700">Config name</span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 block w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm"
              />
            </label>
            <label className="block lg:col-span-2">
              <span className="text-xs font-semibold text-zinc-700">Notes (optional)</span>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="mt-1 block w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm"
              />
            </label>
          </div>

          <label className="mt-4 block">
            <div className="flex items-baseline justify-between">
              <span className="text-xs font-semibold text-zinc-700">Rules JSON</span>
              {parseError && (
                <span className="text-xs text-red-700">parse error: {parseError}</span>
              )}
            </div>
            <textarea
              value={rulesText}
              onChange={onTextChange}
              rows={20}
              spellCheck="false"
              className="mt-1 block w-full rounded-lg border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono text-xs"
            />
          </label>

          <div className="mt-4 flex justify-between">
            <button
              type="button"
              onClick={resetDefaults}
              disabled={busy}
              className="rounded-xl border border-zinc-300 bg-white px-4 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-100 disabled:opacity-50"
            >
              Reload from server
            </button>
            <div className="flex items-center gap-3">
              {savedAt && (
                <span className="text-xs text-zinc-500">
                  saved {new Date(savedAt).toLocaleString()}
                </span>
              )}
              <button
                type="button"
                onClick={save}
                disabled={busy || !!parseError}
                className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {busy ? 'Saving…' : 'Save rules'}
              </button>
            </div>
          </div>
        </section>

        <section className="mt-6 rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
          <h2 className="text-base font-bold text-zinc-900">Field reference</h2>
          <p className="mt-1 text-xs text-zinc-600">
            Use these field names in <code className="rounded bg-zinc-100 px-1 font-mono">conditions[].field</code>.
            Operators: <code className="font-mono">{'>='}</code>, <code className="font-mono">{'<='}</code>,
            {' '}<code className="font-mono">==</code>, <code className="font-mono">!=</code>,
            {' '}<code className="font-mono">{'>'}</code>, <code className="font-mono">{'<'}</code>.
            Stages: {STAGES.join(', ')}. (closed/passed reachable only via outcome marking.)
          </p>
          <ul className="mt-3 space-y-1 text-xs text-zinc-700">
            {FIELD_HINTS.map((h) => (
              <li key={h.field}>
                <code className="rounded bg-zinc-100 px-1 font-mono">{h.field}</code>
                {' — '}
                <span className="text-zinc-600">{h.desc}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
